import { environment } from '../../../environments/environment';
import {
    Component, EventEmitter, ViewChild, OnDestroy, OnInit, OnChanges, SimpleChange, Output, Input, TemplateRef, ContentChild,
    ElementRef, AfterViewInit } from '@angular/core';
import { Subject, Subscription } from 'rxjs';
import { debounceTime } from 'rxjs/operators';
import { DatatableComponent, SortDirection, SelectionType, SortType, DataTableFooterTemplateDirective } from '@swimlane/ngx-datatable';
import { MyDatePipe } from '../date/date.pipe';
import { MyDateTimePipe } from '../date/date-time.pipe';
import { SortFilterDto } from "../../api/model/sortFilterDto";
import { Filter } from "../../api/model/filter";
import { Sort } from "../../api/model/sort";
import DirectionEnum = Sort.DirectionEnum;
import { CurrencyPipe } from '@angular/common';

/** Metadata defining a column in a Data Table */
export interface DataTableColumn {
    /** Display header text */
    header: string;
    /** The row property containing the value */
    property: string;
    /** Filter property */
    filterCol?: string;
    /* The width of the column by default in pixels */
    width: number;
    /**
     * Sorting of the row values by this column. May be:
     * - true = allow user to sort, but initially unsorted. (default)
     * - false = do not sort nor allow user to sort
     * - 'asc' = initialize to sort ascending, and allow user to change.
     * - 'desc' = initialize to sort descending, and allow user to change.
     */
    sortable?: boolean | 'asc' | 'desc';
    /** Enable string filtering by this column. Default value: true */
    filterable?: boolean;
    /** If set, format the values */
    format?: 'integer'|'decimal2'|'USD'|'zeroBlankUSD'|'html'|'date'|'datetime'|'dateMonthYear'|'blankZeroDecimal';
    /**
     * If this value is true, add a class to break a word to prevent overlap.
     */
    wordbreak?: boolean;

    /* Use to provide function to operate on grid cell style */
    getCellClass?: (any: Object) => any;

}

//export const Default_Table_Page_Size = (('production' !== ENV) ? 10 : 100);
export const Default_Table_Page_Size = ((!environment.production) ? 100 : 100);

/** Metadata defining a Data Table */
export interface DataTableDef {
    /** The page size to be shown. Default is 100 in production, 5 in development */
    limit?: number;

    /** ID property in each data row that identifies the unique identifier */
    idProperty?: string;

    /**
     * Height of the detail row. Default is auto.
     * Must also provide a <template let-row="row">, within the <my-data-table> element,
     * which contains the template for the content of an expanded detail row.
     */
    detailRowHeight?: number;

    /** Number of text lines to allocate for the column headers. 1 = 1 line of text, 2 = 2 lines of text */
    columnHeaderLines?: number;

    columns: DataTableColumn[];

    /** A function that will invoked with each row's properties. The result of the function can be a string or object with key/boolean */
    getRowClass?: (row: Object) => any;

    /**
     * Message overrides for localization/customization.
     *
     * emptyMessage     [default] = 'No data to display'
     * totalMessage     [default] = 'total'
     * hideTotal        [default] = false -- used to show or hide the total message if desired
     * selectedMessage  [default] = 'selected'
     */
    messages?: {emptyMessage?: string, totalMessage?: string, hideTotal?: boolean, selectedMessage?: string };

    /**
     * Use this to hide the column headers
     */
    hideColumnHeaders?: boolean;
}

export interface DataTableClick {
    /** The row object (as input) that was clicked upon */
    row: Object;
    /** The property name, within the row & as defined in DataTableColumn, that was clicked */
    property: string;

    id?: string;

}

export const resetPageNumber = new EventEmitter<number>();

type SortSelection = {prop: string, dir: SortDirection};

@Component({
    selector: 'my-data-table',
    templateUrl: './data-table.component.html',
    styles: ['input[type="search"] { width: 100%; padding: 0 4px;}']
})
export class DataTableComponent implements OnInit, OnDestroy, AfterViewInit, OnChanges {
    readonly isDevMode = !environment.production;

    @ViewChild('myTable', {static: false}) table: DatatableComponent;
    @ViewChild('topOfTable', {static: false}) topOfTable: ElementRef;
    @ViewChild('myFooterTemplate', {static: true}) footerTemplate: DataTableFooterTemplateDirective;
  
    @Input()
    set def(value: DataTableDef){
        if(value){
            // Set default messages
            if (!value.messages)
                value.messages = {};
            if (value.messages.emptyMessage === undefined)
                value.messages.emptyMessage = 'No data to display';
            if (value.messages.totalMessage === undefined && !value.messages.hideTotal)
                value.messages.totalMessage = 'total';
            if (this.selections !== undefined && value.messages.selectedMessage === undefined)
                value.messages.selectedMessage = 'selected';
            this.tableDefinition = value;
        }

    }
    tableDefinition: DataTableDef;

    @Input() hideFooter: boolean = false;

    @Input() hidePager: boolean = false;

    @Input() totalRows?: number;

    @Input() displayedRowsCount: string;

    @Input() totalDataRows?: number;

    @Output() fetchNewData = new EventEmitter<SortFilterDto>();

    @Input() externalPaging = false;

    /**
     * Array of rows to show.
     * Set to null to display loading animation.
     * Set to [] if no data.
     */
    @Input()
    set rows(value: Object[]) {
        if (value) {
            this.allRows = value;
            this.displayRows = value;
            if(!this.externalPaging) {               
            setTimeout(() => { 
                this.table.onFooterPage({page: 1});
            });
            this.applyFiltersAndSort();
            }

            setTimeout(() => {       
                //Resize scroller element to fix unwanted horizontal scrollbar - swimlane may fix this in future
                if (this.table) {
                    var scroll = this.table.element.querySelector('.datatable-scroll');
                    if (scroll) {
                        scroll.setAttribute('style', 'width:100%');
                    }
                }
            });
            this.isLoading = false;
        }
        else {
            this.allRows = [];
            this.displayRows = this.allRows;
            this.isLoading = true;
        }
    }

    /**
     * List of row objects that should be represented as selected in the grid.
     * Updated by U/I and visitable to parent.
     */
    @Input() selections?: Object[];

    /** The type of column structure to use.  Force is default, but in some places (like inside tabs)
     * static may be necessary to have more control and avoid table structure failure due to
     * visibility and forced width
     */
    @Input() columnMode: string = 'force';

    /** When a row is clicked, emits the row object */
    @Output() rowClick = new EventEmitter<DataTableClick>();

    @Input() searchText: string = '';

    /** An child <template> element defines the expand content for a row */
    @ContentChild(TemplateRef, {static: false})
    expandTemplateRef: TemplateRef<Object>;

    /** Applied sort selection(s) */
    appliedSort: SortSelection[] = [];

    /** Rows received by 'rows' property */
    allRows: Object[];

    /** Filtered rows */
    displayRows = [];

    /** As an object, contains property values to apply as filters. null to disable filtering */
    filterRow: {} = null;

    isFilterable: boolean = true;

    /** Is the data loading? Show the progress indicator. */
    isLoading: boolean = false;

    selectionType = SelectionType;
    sortType = SortType;

    /** Debounce Observable to hold off on applying filters until user stops typing */
    private filterDebounce = new Subject<boolean>();
    private filterDebounceSubscription: Subscription;
    
    /** Outer components can force page number reset on pager */
    private pageNumberEvent: Subscription;

    /** Cache for getColumnDefByProperty(propName) */
    private columnDefLookup: Map<string, DataTableColumn>;

    private sortFilterDto: SortFilterDto = {
        Filters:[],
        Sorts:[],
        PageNumber: 1,
        PageSize: Default_Table_Page_Size,
        SearchTerm: ''
    };

    pageSize: number;
    page: number=1;

    @Input() showMoreLess = false;

    /**
     * Construct.
     */
    constructor(private e: ElementRef) {
        /*
         * There is a bug in the DataTable component in iOS where columns are resized on scroll.
         * When this happens, the new value is not a number but an event.
         * This causes the columns to have an invalid width and revert to a default width that just contains their content. There is an open issue for this issue.
         * See: https://github.com/swimlane/ngx-datatable/issues/806
         *
         * To combat this issue, since we do not support column reisize in MyRemedi, we will simply set the new value to the previous value (or the current value).
         */
        DatatableComponent.prototype.onColumnResize = function (event) {

            this.resize.emit({
                column: event.column,
                newValue: event.prevValue
            })
        };
    }

    ngOnInit(): void {

        //If outer component wants to reset page number
        this.pageNumberEvent = resetPageNumber.subscribe((n:number) => this.setPageNumber(n));

        //c.log("TotalColumnWidths = "+this.tableDefinition.columns.map(c => c.width).reduce((p,c) => p+c));
        this.pageSize = this.tableDefinition.limit || Default_Table_Page_Size;

        if(this.showMoreLess) {
            this.pageSize = 5;
            this.hidePager = true;
        }

        this.appliedSort = this.tableDefinition.columns
            .filter(c => c.sortable === 'asc' || c.sortable === 'desc')
            .map(c => {
                return {
                    prop: c.property,
                    dir: c.sortable === 'asc' ? SortDirection.asc : SortDirection.desc
                };
            });


        this.isFilterable = !this.tableDefinition.columns.every((colDef) => colDef.filterable === false);
        this.filterDebounceSubscription = this.filterDebounce.pipe(debounceTime(333)).subscribe(() => this.applyFiltersAndSort());
        this.displayRows = this.allRows;

        // Set default messages
        if (!this.tableDefinition.messages)
            this.tableDefinition.messages = {};
        if (this.tableDefinition.messages.emptyMessage === undefined)
            this.tableDefinition.messages.emptyMessage = 'No data to display';
        if (this.tableDefinition.messages.totalMessage === undefined && !this.tableDefinition.messages.hideTotal)
            this.tableDefinition.messages.totalMessage = 'total';
        if (this.selections !== undefined && this.tableDefinition.messages.selectedMessage === undefined)
            this.tableDefinition.messages.selectedMessage = 'selected';
    }

    ngAfterViewInit() {
        let myCheckboxes = this.e.nativeElement.querySelectorAll('input[type=checkbox]');
        for (let checkbox of myCheckboxes) {
            checkbox.insertAdjacentHTML('afterend', '<span></span>');
        }
    }

    ngOnDestroy(): void {
        if (this.filterDebounceSubscription) {
            this.filterDebounceSubscription.unsubscribe();
        }

        if (this.pageNumberEvent) {
            this.pageNumberEvent.unsubscribe();
        }
    }

    /**
     * Callback from ngx-datatable after user clicks a column heading.
     * @param event
     */
    onSort(event: {column: {prop: string}, prevValue: SortDirection, newValue: SortDirection, sorts: SortSelection}) {
        if (event.newValue == null) {
            // Reset to default
            this.appliedSort = [];
        }
        else {
            this.appliedSort = [{
                prop: event.column.prop,
                dir: event.newValue
            }];
        }
        this.applyFiltersAndSort();
    }

    /**
     * User clicked on the chevron to open/close the expand row.
     * @param row
     */
    toggleExpandRow(row: number) {
        this.table.rowDetail.toggleExpandRow(row);
    }

    /**
     * Scroll the page to the top of the table when paginating.
     */
    onPageChange(event: {count: number, pageSize: number, limit: number, offset: number}) {
        let targetPos = -220; // Account for navigation and table headers
        let el = this.topOfTable.nativeElement;
        while(el && !isNaN(el.offsetTop)) {
            targetPos += el.offsetTop;
            el = el.offsetParent;
        }
        this.page = event.offset+1;

        if(this.externalPaging){
            this.fetchExternalData();
        }

        // Scroll, don't jump.
        if (targetPos > 0) {
            let cancelId = window.setInterval(() => {
                let windowScrollY = window.scrollY || /*IE9+*/window.pageYOffset;
                let distance = Math.min(windowScrollY - targetPos, 300);
                if (distance > 0) {
                    window.scrollBy({top: -distance, left: 0, behavior: 'instant'});
                }
                else {
                    window.clearInterval(cancelId);
                }
            }, 0);
        }
    }

    /**
     * User clicked on a row.
     * @param event
     */
    onRowClick(event: { type: string, row: any, column: {prop: string}}) {
        // Was a mouse click on a column that maps to a data property?
        if (event.type === 'click' && event.row && event.column && event.column.prop) {
            let dataTableClick = {row: event.row, property: event.column.prop} as DataTableClick;
            this.rowClick.emit(dataTableClick);
        }
    }

    /**
     * User clicked filter icon to toggle the filter options.
     */
    onToggleFilter() {
        if (this.filterRow) {
            // Hide & clear filters
            this.filterRow = null;
            this.applyFiltersAndSort();
        }
        else {
            // Show filter fields
            this.filterRow = {};
        }
    }

    /**
     * A filter input field has changed.
     */
    updateFilter(): void {
        // Run though debounce filter. Will only process once user pauses for a moment.
        this.filterDebounce.next(true);
    }

    /**
     * Called via filterDebounce after user stops typing for a bit, when a sort is changed, or via onNgChanges when the searchText has changed.
     */
    private applyFiltersAndSort(): void {
        // Make array of property names that have a filter value set

        if(this.externalPaging){
            this.fetchExternalData();
            return;
        }

        let searchFunctions = this.searchText === '' ? [] : this.tableDefinition.columns.filter(f=>f.filterable!=false)
            .map(col => {
                return this.getFilterFor(col, this.searchText);
            });

        let filterFunctions = this.filterRow === null ? []
            : Object.keys(this.filterRow)
                .filter(property => this.filterRow[property] && this.filterRow[property].length)
                .map(property => {
                    let colDef = this.getColumnDefByProperty(property);
                    return this.getFilterFor(colDef, this.filterRow[property]);
                });


        // filter our data
        let filteredRows=[...this.allRows];
        if(searchFunctions.length>0) {
            filteredRows = filteredRows.filter(row => {
                let totalFailure: boolean = false;

                for (let filterFn of searchFunctions) {
                    totalFailure = totalFailure || filterFn(row);
                }
                return totalFailure;
            });
        }
        if (filterFunctions.length > 0) {
            filteredRows = filteredRows.filter(row => {
                for (let filterFn of filterFunctions) {
                    if (filterFn(row) === false)
                        return false;
                }
                return true;
            });
        }

        // Sort rows
        if (this.appliedSort.length > 0) {
            let sortProp: string = this.appliedSort[0].prop;

            let invert: boolean  = (this.appliedSort[0].dir === SortDirection.desc);
            let colDef = this.getColumnDefByProperty(sortProp);
            filteredRows = filteredRows.sort(this.getComparatorFor(colDef, invert));
        }

        // update the rows
        setTimeout(()=> {
            this.displayRows=null;
            this.displayRows = filteredRows;
        });
    }

    /**
     * Build sortFilterDto and fetch from external data source
     */
    private fetchExternalData(): void{
        this.sortFilterDto.SearchTerm = this.searchText || "";

        this.sortFilterDto.Filters = this.filterRow === null ? []
            : Object.keys(this.filterRow)
                .filter(property => this.filterRow[property] && this.filterRow[property].length)
                .map(property => {

                    let filter: Filter =
                        {
                            FilterValue:this.filterRow[property],
                            ColumnName: property
                        }
                    return filter;
                });
        
        // search text is entered and there are no column filters selected, grab all the columns with the search text value as filters
        // This is just for boolean type searches
        if (this.searchText && this.sortFilterDto.Filters.length === 0) {
            this.sortFilterDto.FilterType = SortFilterDto.FilterTypeEnum.Or; // For now this is just for OR type search only
            this.sortFilterDto.Filters = this.tableDefinition.columns
                .filter(property => property.filterCol)
                .map(property => {
                    let filter: Filter =
                    {
                        FilterValue: this.searchText,
                        ColumnName: property.filterCol
                    }
                    return filter;
                });
        }

        if (this.appliedSort.length > 0) {
            this.sortFilterDto.Sorts = [];
            this.appliedSort.forEach((sort: SortSelection) => {
                // TODO: Make all sorts use server enum for convenience and clarity
                let direction: Sort.DirectionEnum;
                switch (sort.dir){
                    case SortDirection.asc:
                        direction = DirectionEnum.Asc;
                        break;
                    case SortDirection.desc:
                        direction = DirectionEnum.Desc;
                        break;
                }
                this.sortFilterDto.Sorts.push({
                    Direction: direction,
                    ColumnName: sort.prop
                })
            })
        }else{
            this.sortFilterDto.Sorts = [];
        }

        this.sortFilterDto.PageNumber=this.page;
        this.sortFilterDto.PageSize = this.pageSize;

        this.fetchNewData.emit(this.sortFilterDto);
    }

    /**
     * Create a sorting comparator for a column.
     *
     * @param columnDef the DataTableColumn to sort
     * @param invert true if sort decending instead of ascending`
     * @returns comparator function
     */
    private getComparatorFor(columnDef: DataTableColumn, invert: boolean): (a: Object, b: Object)=>number {
        const property = columnDef.property;
        switch (columnDef.format) {
            case 'integer':
            case 'decimal2':
            case 'USD':
                return (a: Object, b: Object) => {
                    let comp = parseFloat(a[property]) - parseFloat(b[property]);
                    if (!isFinite(comp))
                        comp = 0;
                    return invert ? -comp : comp;
                };
            case 'html':
            case 'date':
            case 'datetime':
            default:
                return (a: Object, b: Object) => {
                    let av = String(a[property]).toLowerCase();
                    let bv = String(b[property]).toLowerCase();
                    let comp = av < bv ? -1 : av > bv ? 1 : 0;
                    return invert ? -comp : comp;
                };
        }
    }

    /**
     * Create a filtering function for a column
     * @param columnDef the DataTableColumn to sort
     * @param filterStr value filter on
     * @returns filter function
     */
    private getFilterFor(columnDef: DataTableColumn, filterStr: string): (a: Object)=>boolean {
        const property = columnDef.property;
        switch (columnDef.format) {
            case 'integer':
            case 'decimal2': {
                const filterValue = Number(filterStr);
                return (a: Object) => (a[property] === filterValue);
            }
            case 'USD': {
                const filterValue = filterStr.toUpperCase();
                const formatter = new CurrencyPipe('en');
                return (a:Object) => {
                    let hasDollarSign = filterValue.indexOf('$') !== -1;
                    let currencyString = formatter.transform(a[property], 'USD', hasDollarSign);
                    if (currencyString)
                        return currencyString.indexOf(filterValue) !== -1;
                    return false;
                }
            }
            case 'date': {
                const filterValue = filterStr.toUpperCase();
                const formatter = new MyDatePipe();
                return (a: Object) => {
                    let dateString = formatter.transform(a[property]);
                    if(dateString)
                        return dateString.indexOf(filterValue) !== -1;
                    return false;
                };
            }
            case 'datetime': {
                const filterValue = filterStr.toUpperCase();
                const formatter = new MyDateTimePipe();
                return (a: Object) => {
                    let dateString = formatter.transform(a[property]);
                    if(dateString)
                        return dateString.indexOf(filterValue) !== -1;
                    return false;
                };
            }
            case 'html':
            default: {
                const filterValue = filterStr.toLowerCase();
                return (a: Object) => {
                    var value: string = ''+a[property];
                    return (value.toLowerCase().indexOf(filterValue) !== -1)
                };
            }
        }
    }

    checkSelected(selected : any){
        let idProp = this.tableDefinition.idProperty;

        if(this.selections.find(row => row[idProp] === selected)){
            return true;
        }
        return false;
    }

    selectRow(selected) {
        let idProp = this.tableDefinition.idProperty;

        let currentRow = this.selections.find(row => row[idProp] === selected);
        let index = -1;
        if (currentRow) {
            index = this.selections.indexOf(currentRow);
        }else{
            currentRow = this.allRows.find(row => row[idProp] === selected);
        }

        if(index !== -1){
            this.selections.splice(index, 1);
        }else{
            this.selections.push(currentRow);
        }
    }

    /**
     * Get the DataTableColumn structure defining a column by its property.
     * @param propName
     * @returns {undefined|DataTableColumn}
     */
    private getColumnDefByProperty(propName: string): DataTableColumn {
        if (this.columnDefLookup === undefined) {
            this.columnDefLookup = new Map<string, DataTableColumn>();
            this.tableDefinition.columns.forEach((col) => this.columnDefLookup.set(col.property, col));
        }

        return this.columnDefLookup.get(propName);
    }

    ngOnChanges(changes: {[propName: string]: SimpleChange}) {
        let searchTextChange=changes['searchText'];
        if (searchTextChange && !searchTextChange.isFirstChange()) {
            if(searchTextChange.currentValue != searchTextChange.previousValue){
                this.searchText = searchTextChange.currentValue ? searchTextChange.currentValue : '';
                this.filterDebounce.next(true);
            }
        }
    }

    showMore (){
        this.pageSize = 50;
        this.sortFilterDto.PageSize = this.pageSize;
        this.hidePager = false;

        this.onPageChange({pageSize: this.pageSize, limit: 0, count: 0, offset:0})
    }

    showLess () {
        this.pageSize = 5;
        this.sortFilterDto.PageSize = this.pageSize;
        this.hidePager = true;

        this.onPageChange({pageSize: this.pageSize, limit: 0, count: 0, offset:0})
    }

    setPageNumber(pageNumber: number) {
        this.page = pageNumber;
    }

    getDisplayedRowsCount(){
        if (this.displayedRowsCount){
            return this.displayedRowsCount;
        }
        return this.totalRows ? this.totalRows : this.displayRows.length;
    }
}